// ============================================================================
// SHARED ANALYTICS PRESENTATIONAL COMPONENTS
// ============================================================================
// Module-level (closure-free) building blocks shared across the analytics
// sections: KPI tiles + sparklines, export/date-range/annotation controls,
// chart wrappers, section headers, and the stage data widgets. Extracted from
// AnalyticsDashboardPage to shrink it; behavior is unchanged.
// ============================================================================

import React, { useState, useEffect } from 'react';
import { TrendingUp, TrendingDown, ChevronDown, ChevronUp, AlertTriangle, CheckCircle } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { BrioDatePicker, BrioDateRangePicker } from '../../../components/BrioComponents';
import { supabase } from '../../../supabaseClient';
import { brioColors, brioStyles } from './theme';

const ExportButton = ({ onClick, label, icon = '↓' }) => (
  <button
    onClick={onClick}
    style={{
      padding: `${brioStyles.spacingXS} ${brioStyles.spacingS}`,
      background: brioColors.white,
      border: `1px solid ${brioColors.border}`,
      color: brioColors.black,
      fontSize: '11px',
      fontWeight: brioStyles.fontWeightRegular,
      cursor: 'pointer',
      fontFamily: brioStyles.fontFamily,
      textTransform: 'lowercase',
      borderRadius: '0',
      display: 'flex',
      alignItems: 'center',
      gap: '6px'
    }}
  >
    <span>{icon}</span>
    {label}
  </button>
);

const ExportPanel = ({ dateRange, onExportCSV, onExportPDF }) => {
  const [showPanel, setShowPanel] = useState(false);

  const bounds = (() => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    let start, end;

    switch (dateRange.preset) {
      case 'today':
        start = end = today;
        break;
      case 'yesterday':
        start = end = new Date(today.getTime() - 86400000);
        break;
      case 'last7days':
        start = new Date(today.getTime() - 6 * 86400000);
        end = today;
        break;
      case 'last30days':
        start = new Date(today.getTime() - 29 * 86400000);
        end = today;
        break;
      case 'thisMonth':
        start = new Date(now.getFullYear(), now.getMonth(), 1);
        end = today;
        break;
      case 'lastMonth':
        start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        end = new Date(now.getFullYear(), now.getMonth(), 0);
        break;
      case 'thisQuarter': {
        const quarterStartMonth = Math.floor(now.getMonth() / 3) * 3;
        start = new Date(now.getFullYear(), quarterStartMonth, 1);
        end = today;
        break;
      }
      case 'lastQuarter': {
        const lastQuarterStartMonth = Math.floor(now.getMonth() / 3) * 3 - 3;
        start = new Date(now.getFullYear(), lastQuarterStartMonth, 1);
        end = new Date(now.getFullYear(), lastQuarterStartMonth + 3, 0);
        break;
      }
      case 'last12months':
        start = new Date(now.getFullYear() - 1, now.getMonth(), now.getDate());
        end = today;
        break;
      case 'lastYear':
        start = new Date(now.getFullYear() - 1, 0, 1);
        end = new Date(now.getFullYear() - 1, 11, 31);
        break;
      case 'last5years':
        start = new Date(now.getFullYear() - 5, 0, 1);
        end = today;
        break;
      case 'custom':
        start = dateRange.startDate ? new Date(dateRange.startDate) : new Date(now.getFullYear(), 0, 1);
        end = dateRange.endDate ? new Date(dateRange.endDate) : today;
        break;
      case 'allTime':
      default:
        start = new Date(2020, 0, 1);
        end = today;
        break;
    }

    return { start, end };
  })();

  const formatDate = (date) => {
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      year: 'numeric'
    });
  };

  return (
    <div style={{ marginBottom: brioStyles.spacingM }}>
      <button
        onClick={() => setShowPanel(!showPanel)}
        style={{
          padding: `${brioStyles.spacingS} ${brioStyles.spacingM}`,
          background: showPanel ? brioColors.gold : brioColors.white,
          border: `1px solid ${showPanel ? brioColors.gold : brioColors.border}`,
          color: showPanel ? brioColors.white : brioColors.black,
          fontSize: brioStyles.fontSizeBase,
          fontWeight: brioStyles.fontWeightRegular,
          cursor: 'pointer',
          fontFamily: brioStyles.fontFamily,
          textTransform: 'lowercase',
          borderRadius: '0'
        }}
      >
        {showPanel ? 'hide' : 'show'} export options
      </button>

      {showPanel && (
        <div style={{
          marginTop: brioStyles.spacingS,
          padding: brioStyles.spacingM,
          background: brioColors.greyLight,
          border: `1px solid ${brioColors.border}`
        }}>
          <div style={{
            fontSize: '11px',
            color: brioColors.grey,
            marginBottom: brioStyles.spacingS,
            fontWeight: brioStyles.fontWeightLight,
            fontStyle: 'italic'
          }}>
            exports will include data from {formatDate(bounds.start)} to {formatDate(bounds.end)}
          </div>
          <div style={{
            display: 'flex',
            gap: brioStyles.spacingS,
            flexWrap: 'wrap'
          }}>
            <ExportButton onClick={onExportCSV} label="export as csv" icon="📊" />
            <ExportButton onClick={onExportPDF} label="export as pdf" icon="📄" />
          </div>
        </div>
      )}
    </div>
  );
};

// ============================================================================
// DATE RANGE FILTER COMPONENT
// ============================================================================

const DateRangeFilter = ({ dateRange, onChange }) => {
  const [showCustom, setShowCustom] = useState(false);
  const [showComparison, setShowComparison] = useState(false);

  const presets = [
    { id: 'today', label: 'today' },
    { id: 'yesterday', label: 'yesterday' },
    { id: 'last7days', label: 'last 7 days' },
    { id: 'last30days', label: 'last 30 days' },
    { id: 'thisMonth', label: 'this month' },
    { id: 'lastMonth', label: 'last month' },
    { id: 'thisQuarter', label: 'this quarter' },
    { id: 'lastQuarter', label: 'last quarter' },
    { id: 'last12months', label: 'last 12 months' },
    { id: 'lastYear', label: 'last year' },
    { id: 'last5years', label: 'last 5 years' },
    { id: 'allTime', label: 'all time' },
    { id: 'custom', label: 'custom range' }
  ];

  const handlePresetChange = (presetId) => {
    onChange({ ...dateRange, preset: presetId });
    if (presetId === 'custom') {
      setShowCustom(true);
    } else {
      setShowCustom(false);
    }
  };

  return (
    <div style={{
      marginBottom: brioStyles.spacingM
    }}>
      {/* Compact Single Row Filter */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: brioStyles.spacingS,
        flexWrap: 'wrap'
      }}>
        <span style={{
          fontSize: '11px',
          color: brioColors.grey,
          fontWeight: brioStyles.fontWeightRegular,
          fontFamily: brioStyles.fontFamily,
          textTransform: 'lowercase'
        }}>
          period:
        </span>
        
        <select
          value={dateRange.preset}
          onChange={(e) => handlePresetChange(e.target.value)}
          style={{
            padding: '6px 32px 6px 12px',
            border: `1px solid ${brioColors.border}`,
            fontSize: '11px',
            fontFamily: brioStyles.fontFamily,
            fontWeight: brioStyles.fontWeightRegular,
            background: brioColors.white,
            color: brioColors.black,
            borderRadius: '0',
            cursor: 'pointer',
            textTransform: 'lowercase',
            appearance: 'none',
            backgroundImage: `url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%23666666' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'%3e%3c/polyline%3e%3c/svg%3e")`,
            backgroundRepeat: 'no-repeat',
            backgroundPosition: 'right 8px center',
            backgroundSize: '14px'
          }}
        >
          {presets.map(preset => (
            <option key={preset.id} value={preset.id}>
              {preset.label}
            </option>
          ))}
        </select>

        {(showCustom || dateRange.preset === 'custom') && (
          <BrioDateRangePicker
            startDate={dateRange.startDate}
            endDate={dateRange.endDate}
            onChangeStart={v => onChange({ ...dateRange, startDate: v })}
            onChangeEnd={v => onChange({ ...dateRange, endDate: v })}
            clearable={false}
          />
        )}

        {/* Compare Button */}
        <button
          onClick={() => setShowComparison(!showComparison)}
          style={{
            padding: '6px 12px',
            background: showComparison ? brioColors.gold : brioColors.white,
            border: `1px solid ${showComparison ? brioColors.gold : brioColors.border}`,
            color: showComparison ? brioColors.white : brioColors.black,
            fontSize: '11px',
            fontWeight: brioStyles.fontWeightRegular,
            cursor: 'pointer',
            fontFamily: brioStyles.fontFamily,
            textTransform: 'lowercase',
            borderRadius: '0',
            whiteSpace: 'nowrap',
            transition: 'all 0.2s'
          }}
        >
          compare
        </button>

        {showComparison && (
          <>
            <label style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              fontSize: '11px',
              color: brioColors.grey,
              fontFamily: brioStyles.fontFamily,
              cursor: 'pointer'
            }}>
              <input
                type="checkbox"
                checked={dateRange.compareEnabled}
                onChange={(e) => onChange({ ...dateRange, compareEnabled: e.target.checked })}
                style={{ 
                  cursor: 'pointer',
                  width: '14px',
                  height: '14px'
                }}
              />
              <span>enable</span>
            </label>

            {dateRange.compareEnabled && (
              <BrioDateRangePicker
                startDate={dateRange.compareStartDate}
                endDate={dateRange.compareEndDate}
                onChangeStart={v => onChange({ ...dateRange, compareStartDate: v })}
                onChangeEnd={v => onChange({ ...dateRange, compareEndDate: v })}
                clearable={false}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
};

// ============================================================================
// ANNOTATION SYSTEM
// ============================================================================

// Annotation types - BRIO aligned colors and text markers (no emojis)
const annotationTypes = {
  office: { 
    color: brioColors.gold, 
    marker: 'office', 
    label: 'office opening',
    symbol: '●'
  },
  strategy: { 
    color: brioColors.goldHover, 
    marker: 'strategy', 
    label: 'strategic shift',
    symbol: '▲'
  },
  crisis: { 
    color: '#8b5cf6', 
    marker: 'crisis', 
    label: 'crisis period',
    symbol: '■'
  },
  milestone: { 
    color: brioColors.black, 
    marker: 'milestone', 
    label: 'milestone',
    symbol: '◆'
  },
  policy: { 
    color: brioColors.grey, 
    marker: 'policy', 
    label: 'policy change',
    symbol: '○'
  }
};

// Annotation component for charts
const _ChartAnnotation = ({ date, type, title, description, x, y }) => { // eslint-disable-line no-unused-vars
  const [showTooltip, setShowTooltip] = useState(false);
  const annotation = annotationTypes[type];

  return (
    <g>
      {/* Vertical line */}
      <line
        x1={x}
        y1={0}
        x2={x}
        y2={y}
        stroke={annotation.color}
        strokeWidth="1"
        strokeDasharray="4 4"
        opacity={0.5}
      />
      {/* Marker symbol */}
      <text
        x={x}
        y={15}
        textAnchor="middle"
        fill={annotation.color}
        fontSize="14"
        fontWeight="400"
        fontFamily={brioStyles.fontFamily}
        style={{ cursor: 'pointer' }}
        onMouseEnter={() => setShowTooltip(true)}
        onMouseLeave={() => setShowTooltip(false)}
      >
        {annotation.symbol}
      </text>
      {/* Tooltip on hover */}
      {showTooltip && (
        <foreignObject x={x - 100} y={25} width="200" height="100">
          <div style={{
            background: brioColors.white,
            border: `1px solid ${annotation.color}`,
            padding: brioStyles.spacingS,
            borderRadius: '0',
            fontSize: '11px',
            fontFamily: brioStyles.fontFamily,
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
          }}>
            <div style={{ 
              fontWeight: brioStyles.fontWeightMedium,
              color: annotation.color,
              marginBottom: '4px',
              textTransform: 'lowercase'
            }}>
              {annotation.label}
            </div>
            <div style={{ 
              fontWeight: brioStyles.fontWeightMedium,
              color: brioColors.black,
              marginBottom: '2px'
            }}>
              {title}
            </div>
            <div style={{ 
              color: brioColors.grey,
              fontSize: '10px'
            }}>
              {date}
            </div>
            {description && (
              <div style={{ 
                color: brioColors.grey,
                fontSize: '10px',
                marginTop: '4px',
                fontStyle: 'italic',
                fontWeight: brioStyles.fontWeightLight
              }}>
                {description}
              </div>
            )}
          </div>
        </foreignObject>
      )}
    </g>
  );
};

// Annotation management component
const AnnotationManager = ({ annotations, onAdd, onDelete, onToggle, pendingDeleteId }) => {
  const [showManager, setShowManager] = useState(false);
  const [newAnnotation, setNewAnnotation] = useState({
    date: '',
    type: 'milestone',
    title: '',
    description: ''
  });
  // FIX 8: State for validation message instead of alert
  const [validationMessage, setValidationMessage] = useState('');

  return (
    <div style={{ marginBottom: brioStyles.spacingM }}>
      <button
        onClick={() => setShowManager(!showManager)}
        style={{
          padding: `${brioStyles.spacingXS} ${brioStyles.spacingS}`,
          background: showManager ? brioColors.gold : 'transparent',
          border: `1px solid ${brioColors.gold}`,
          color: showManager ? brioColors.white : brioColors.gold,
          fontSize: '11px',
          fontWeight: brioStyles.fontWeightRegular,
          cursor: 'pointer',
          fontFamily: brioStyles.fontFamily,
          textTransform: 'lowercase',
          borderRadius: '0',
          display: 'flex',
          alignItems: 'center',
          gap: '6px'
        }}
      >
        <span style={{ fontSize: '14px' }}>●</span>
        {showManager ? 'hide' : 'show'} annotations ({annotations.length})
      </button>

      {showManager && (
        <div style={{
          marginTop: brioStyles.spacingS,
          padding: brioStyles.spacingM,
          background: brioColors.greyLight,
          border: `1px solid ${brioColors.border}`
        }}>
          {/* Add new annotation form */}
          <div style={{ marginBottom: brioStyles.spacingM }}>
            <h4 style={{
              fontSize: brioStyles.fontSizeBase,
              fontWeight: brioStyles.fontWeightMedium,
              color: brioColors.black,
              marginBottom: brioStyles.spacingS,
              textTransform: 'lowercase'
            }}>
              add annotation
            </h4>
            
            <div style={{ display: 'grid', gap: brioStyles.spacingXS }}>
              <BrioDatePicker
                value={newAnnotation.date}
                onChange={v => setNewAnnotation({ ...newAnnotation, date: v })}
                placeholder="Date"
                clearable={false}
              />
              
              <select
                value={newAnnotation.type}
                onChange={(e) => setNewAnnotation({ ...newAnnotation, type: e.target.value })}
                style={{
                  padding: brioStyles.spacingXS,
                  border: `1px solid ${brioColors.border}`,
                  fontSize: '11px',
                  fontFamily: brioStyles.fontFamily,
                  fontWeight: brioStyles.fontWeightRegular,
                  background: brioColors.white,
                  borderRadius: '0',
                  textTransform: 'lowercase'
                }}
              >
                {Object.entries(annotationTypes).map(([key, value]) => (
                  <option key={key} value={key}>
                    {value.symbol} {value.label}
                  </option>
                ))}
              </select>
              
              <input
                type="text"
                placeholder="title (e.g., mumbai office opens)"
                value={newAnnotation.title}
                onChange={(e) => setNewAnnotation({ ...newAnnotation, title: e.target.value })}
                style={{
                  padding: brioStyles.spacingXS,
                  border: `1px solid ${brioColors.border}`,
                  fontSize: '11px',
                  fontFamily: brioStyles.fontFamily,
                  fontWeight: brioStyles.fontWeightRegular,
                  background: brioColors.white,
                  borderRadius: '0'
                }}
              />
              
              <textarea
                placeholder="description (optional)"
                value={newAnnotation.description}
                onChange={(e) => setNewAnnotation({ ...newAnnotation, description: e.target.value })}
                style={{
                  padding: brioStyles.spacingXS,
                  border: `1px solid ${brioColors.border}`,
                  fontSize: '11px',
                  fontFamily: brioStyles.fontFamily,
                  fontWeight: brioStyles.fontWeightRegular,
                  background: brioColors.white,
                  minHeight: '60px',
                  resize: 'vertical',
                  borderRadius: '0'
                }}
              />
              
              <button
                onClick={() => {
                  if (newAnnotation.date && newAnnotation.title) {
                    onAdd({
                      ...newAnnotation,
                      id: Date.now(),
                      enabled: true
                    });
                    setNewAnnotation({ date: '', type: 'milestone', title: '', description: '' });
                    setValidationMessage('');
                  } else {
                    // FIX 8: Use inline message instead of alert
                    setValidationMessage('please fill in date and title');
                  }
                }}
                style={{
                  padding: brioStyles.spacingXS,
                  background: brioColors.gold,
                  border: 'none',
                  color: brioColors.white,
                  fontSize: '11px',
                  fontWeight: brioStyles.fontWeightMedium,
                  cursor: 'pointer',
                  fontFamily: brioStyles.fontFamily,
                  textTransform: 'lowercase',
                  borderRadius: '0'
                }}
              >
                add annotation
              </button>
              
              {/* FIX 8: Inline validation message */}
              {validationMessage && (
                <div style={{
                  marginTop: brioStyles.spacingXS,
                  padding: brioStyles.spacingXS,
                  background: '#fef2f2',
                  border: '1px solid #fecaca',
                  color: '#dc2626',
                  fontSize: '11px',
                  fontFamily: brioStyles.fontFamily
                }}>
                  {validationMessage}
                </div>
              )}
            </div>
          </div>

          {/* Existing annotations list */}
          <div>
            <h4 style={{
              fontSize: brioStyles.fontSizeBase,
              fontWeight: brioStyles.fontWeightMedium,
              color: brioColors.black,
              marginBottom: brioStyles.spacingS,
              textTransform: 'lowercase'
            }}>
              existing annotations
            </h4>

            {annotations.length === 0 ? (
              <p style={{
                fontSize: '11px',
                color: brioColors.grey,
                fontStyle: 'italic',
                fontWeight: brioStyles.fontWeightLight,
                margin: 0
              }}>
                no annotations yet. add one above to mark important events.
              </p>
            ) : (
              <div style={{ display: 'grid', gap: brioStyles.spacingXS }}>
                {annotations.map(ann => {
                  const annType = annotationTypes[ann.type];
                  return (
                    <div
                      key={ann.id}
                      style={{
                        padding: brioStyles.spacingS,
                        background: brioColors.white,
                        border: `1px solid ${annType.color}`,
                        display: 'grid',
                        gridTemplateColumns: 'auto 1fr auto auto',
                        gap: brioStyles.spacingS,
                        alignItems: 'center'
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={ann.enabled}
                        onChange={() => onToggle(ann.id)}
                        style={{ cursor: 'pointer' }}
                      />
                      
                      <div>
                        <div style={{
                          fontSize: '11px',
                          fontWeight: brioStyles.fontWeightMedium,
                          color: brioColors.black,
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px'
                        }}>
                          <span style={{ color: annType.color, fontSize: '14px' }}>
                            {annType.symbol}
                          </span>
                          <span>{ann.title}</span>
                        </div>
                        <div style={{
                          fontSize: '10px',
                          color: brioColors.grey,
                          fontWeight: brioStyles.fontWeightLight
                        }}>
                          {new Date(ann.date).toLocaleDateString('en-US', { 
                            year: 'numeric', 
                            month: 'short', 
                            day: 'numeric' 
                          })}
                        </div>
                      </div>
                      
                      <span style={{
                        fontSize: '10px',
                        padding: '2px 8px',
                        background: annType.color + '15',
                        color: annType.color,
                        fontWeight: brioStyles.fontWeightRegular,
                        textTransform: 'lowercase',
                        fontFamily: brioStyles.fontFamily
                      }}>
                        {annType.label}
                      </span>
                      
                      <button
                        onClick={() => onDelete(ann.id)}
                        style={{
                          padding: '4px 8px',
                          // FIX 8: Show pending state for delete confirmation
                          background: pendingDeleteId === ann.id ? '#dc2626' : 'transparent',
                          border: `1px solid ${pendingDeleteId === ann.id ? '#dc2626' : brioColors.border}`,
                          color: pendingDeleteId === ann.id ? brioColors.white : brioColors.grey,
                          fontSize: '10px',
                          cursor: 'pointer',
                          fontFamily: brioStyles.fontFamily,
                          fontWeight: brioStyles.fontWeightRegular,
                          textTransform: 'lowercase',
                          borderRadius: '0'
                        }}
                      >
                        {pendingDeleteId === ann.id ? 'click to confirm' : 'delete'}
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// ============================================================================
// CUSTOM TOOLTIP COMPONENT
// ============================================================================

const CustomTooltip = ({ active, payload, label, formatCurrency }) => {
  if (!active || !payload || !payload.length) return null;
  const fmtMoney = formatCurrency || _fmtCurrFallback;

  return (
    <div className="brio-tooltip">
      <p className="brio-tooltip__title">
        {label}
      </p>
      {payload.map((entry, index) => (
        <p key={index} className="brio-tooltip__text" style={{ color: entry.color }}>
          {entry.name}: {typeof entry.value === 'number'
            ? (entry.name.includes('₹') || entry.name.toLowerCase().includes('revenue') || entry.name.toLowerCase().includes('cost') || entry.name.toLowerCase().includes('profit')
              ? fmtMoney(entry.value)
              : entry.name.includes('%')
                ? fmtPercent(entry.value)
                : entry.value.toFixed(1))
            : entry.value}
        </p>
      ))}
    </div>
  );
};

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

// REMOVED: Hardcoded fmtCurr — now received as prop from App.js (delegates to settingsHook.formatMoney)
// Fallback retained for backward compatibility if prop not provided.
const _fmtCurrFallback = (amount) => `₹${Math.round(amount).toLocaleString('en-IN')}`;
const fmtPercent = (value) => {
  const abs = Math.abs(value);
  return abs < 10 ? `${value.toFixed(1)}%` : `${Math.round(value)}%`;
};
const _fmtHours = (seconds) => { // eslint-disable-line no-unused-vars
  const hours = seconds / 3600;
  return `${Math.round(hours)}h`;
};

// Generate last N years
// Last `n` *complete* calendar years, excluding the in-progress current year.
// Per-year KPI totals (revenue/FTE, profit/FTE, …) would otherwise compare a
// partial current year against full prior years and show a misleading drop, so
// the trend sparklines and their year-over-year arrows use complete years only.
// The live current-year figure is still shown as each tile's headline value.
// Stage hours chart — uses mv_stage_intelligence materialized view
const StageHoursChart = ({ brioColors, brioStyles, fmtCurr }) => {
  const [stageData, setStageData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const fetchData = async () => {
      try {
        // Try mv_stage_intelligence first (materialized view)
        let { data, error } = await supabase
          .from('mv_stage_intelligence')
          .select('stage_id, hours_total, project_count')
          .order('stage_id');

        // Fallback to stage_window_ledger if mv_stage_intelligence is empty or errors
        if (error || !data || data.length === 0) {
          const res = await supabase
            .from('stage_window_ledger')
            .select('stage_id, hours_total');
          if (!res.error && res.data && res.data.length > 0) {
            // Aggregate by stage_id since stage_window_ledger has per-project rows
            const agg = {};
            res.data.forEach(d => {
              const sid = d.stage_id || 'unknown';
              if (!agg[sid]) agg[sid] = { hours_total: 0, project_count: 0 };
              agg[sid].hours_total += (d.hours_total || 0);
              agg[sid].project_count += 1;
            });
            data = Object.entries(agg).map(([stage_id, v]) => ({
              stage_id, hours_total: v.hours_total, project_count: v.project_count
            }));
          }
        }

        if (!cancelled) {
          setStageData((data || [])
            .filter(d => d.hours_total > 0)
            .map(d => ({
              name: (d.stage_id || 'unknown').replace(/_/g, ' '),
              hours: Math.round(d.hours_total),
              projects: d.project_count || 0,
            }))
          );
        }
      } catch (err) {
        console.error('[StageHoursChart] fetch error:', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchData();
    return () => { cancelled = true; };
  }, []);

  if (loading) return <div style={{ padding: 20, fontSize: 11, color: brioColors.grey }}>loading stage data…</div>;
  if (stageData.length === 0) return <div style={{ padding: 20, fontSize: 11, color: brioColors.grey }}>no stage data available — run "refresh views" in stage intelligence</div>;

  return (
    <ResponsiveContainer width="100%" height={200}>
      <BarChart data={stageData}>
        <CartesianGrid strokeDasharray="3 3" stroke={brioColors.border} />
        <XAxis
          dataKey="name"
          stroke={brioColors.grey}
          style={{ fontSize: '10px', fontFamily: brioStyles.fontFamily }}
          angle={-45}
          textAnchor="end"
          height={80}
        />
        <YAxis
          stroke={brioColors.grey}
          style={{ fontSize: '11px', fontFamily: brioStyles.fontFamily }}
        />
        <Tooltip
          contentStyle={{ fontSize: 11, fontFamily: brioStyles.fontFamily, border: `1px solid ${brioColors.border}` }}
          formatter={(val, name) => [name === 'hours' ? `${val}h` : val, name]}
          cursor={false}
        />
        <Bar dataKey="hours" fill={brioColors.goldHover} activeBar={false} />
      </BarChart>
    </ResponsiveContainer>
  );
};

// Per-chart expand wrapper — renders inline normally, fullscreen overlay when expanded
const ChartExpandWrapper = ({ id, expandedChart, setExpandedChart, children, title }) => {
  const isExpanded = expandedChart === id;
  if (isExpanded) {
    return (
      <div style={{
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
        background: 'rgba(255,255,255,0.97)', zIndex: 9999,
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
        padding: '2vh 2vw',
      }}>
        <div style={{ width: '90vw', height: '90vh', maxWidth: '90vw', maxHeight: '90vh', position: 'relative', display: 'flex', flexDirection: 'column' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8, flexShrink: 0 }}>
            {title && <div style={{ fontSize: 14, fontWeight: 500, textTransform: 'lowercase' }}>{title}</div>}
            <button
              onClick={() => setExpandedChart(null)}
              style={{
                background: 'none', border: 'none', cursor: 'pointer',
                fontSize: 14, color: '#666', fontFamily: 'Raleway, sans-serif',
              }}
            >
              ✕ close
            </button>
          </div>
          <div style={{ flex: 1, width: '100%', minHeight: 0, overflow: 'auto' }}>
            {children}
          </div>
        </div>
      </div>
    );
  }
  return (
    <div style={{ position: 'relative' }}>
      <button
        onClick={() => setExpandedChart(id)}
        title="expand"
        style={{
          position: 'absolute', top: 4, right: 4, zIndex: 2,
          background: 'none', border: 'none', cursor: 'pointer',
          fontSize: 12, color: '#999', padding: 2, lineHeight: 1,
        }}
      >
        ⛶
      </button>
      {children}
    </div>
  );
};

// ============================================================================
// KPI TILE COMPONENT
// ============================================================================

const KPITile = ({ 
  title, 
  value, 
  trend, 
  trendValue, 
  historicalData = [], 
  annotations = [],
  unit = '',
  invertColors = false 
}) => {
  const [showHistory, setShowHistory] = useState(false);
  
  // BRIO-aligned trend colors: use grey for neutral trends
  const trendColor = invertColors 
    ? (trend === 'up' ? brioColors.grey : trend === 'down' ? brioColors.black : brioColors.grey)
    : (trend === 'up' ? brioColors.black : trend === 'down' ? brioColors.grey : brioColors.grey);
  
  return (
    <div className="brio-analytics-kpi">
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'flex-start',
        marginBottom: brioStyles.spacingS 
      }}>
        <div style={{ flex: 1 }}>
          <div className="brio-analytics-metric-label" style={{ marginBottom: '4px' }}>
            {title}
          </div>
          <div className="brio-analytics-value">
            {value}{unit}
          </div>
        </div>
        
        {trend && (
          <div className="brio-analytics-muted" style={{
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            color: trendColor
          }}>
            {trend === 'up' ? <TrendingUp size={16} /> : <TrendingDown size={16} />}
            {trendValue}
          </div>
        )}
      </div>
      
      {historicalData.length > 0 && (
        <>
          <div 
            onClick={() => setShowHistory(!showHistory)}
            style={{
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              marginTop: brioStyles.spacingXS
            }}
            className="brio-analytics-link"
          >
            {showHistory ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
            {showHistory ? 'hide' : 'show'} 5-year trend
            {annotations.length > 0 && ` (${annotations.length} events)`}
          </div>
          
          {showHistory && (
            <div style={{ marginTop: brioStyles.spacingS }}>
              <SimpleTrendLine 
                data={historicalData} 
                height={60}
                annotations={annotations}
              />
            </div>
          )}
        </>
      )}
    </div>
  );
};

// ============================================================================
// SIMPLE TREND LINE (Sparkline) with Annotations
// ============================================================================

const SimpleTrendLine = ({ data, height = 60, annotations = [] }) => {
  if (!data || data.length === 0) return null;
  
  const values = data.map(d => d.value);
  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min || 1;
  
  const points = data.map((d, i) => {
    const x = (i / (data.length - 1)) * 100;
    const y = ((max - d.value) / range) * 100;
    return `${x},${y}`;
  }).join(' ');
  
  // Map annotations to chart positions
  const chartAnnotations = annotations.map(ann => {
    const annYear = new Date(ann.date).getFullYear();
    const dataIndex = data.findIndex(d => d.year === annYear);
    if (dataIndex === -1) return null;
    
    const x = (dataIndex / (data.length - 1)) * 100;
    const annType = annotationTypes[ann.type];
    
    return {
      ...ann,
      x,
      color: annType.color,
      symbol: annType.symbol
    };
  }).filter(a => a !== null);
  
  return (
    <div style={{ position: 'relative' }}>
      <svg 
        viewBox="0 0 100 100" 
        style={{ width: '100%', height: `${height}px` }}
        preserveAspectRatio="none"
      >
        <polyline
          points={points}
          fill="none"
          stroke={brioColors.gold}
          strokeWidth="2"
          vectorEffect="non-scaling-stroke"
        />
        {data.map((d, i) => {
          const x = (i / (data.length - 1)) * 100;
          const y = ((max - d.value) / range) * 100;
          return (
            <circle
              key={i}
              cx={x}
              cy={y}
              r="1.5"
              fill={brioColors.goldHover}
            />
          );
        })}
        
        {/* Annotation markers */}
        {chartAnnotations.map((ann, idx) => (
          <g key={idx}>
            <line
              x1={ann.x}
              y1="0"
              x2={ann.x}
              y2="100"
              stroke={ann.color}
              strokeWidth="1"
              strokeDasharray="2 2"
              opacity="0.4"
            />
            <title>{ann.title} ({new Date(ann.date).getFullYear()})</title>
          </g>
        ))}
      </svg>
      
      {/* Annotation labels below chart */}
      {chartAnnotations.length > 0 && (
        <div style={{
          display: 'flex',
          gap: '8px',
          marginTop: '4px',
          flexWrap: 'wrap'
        }}>
          {chartAnnotations.map((ann, idx) => (
            <div
              key={idx}
              style={{
                fontSize: '9px',
                color: ann.color,
                display: 'flex',
                alignItems: 'center',
                gap: '3px',
                fontWeight: brioStyles.fontWeightRegular,
                fontFamily: brioStyles.fontFamily
              }}
              title={`${ann.title} - ${ann.description || ''}`}
            >
              <span style={{ fontSize: '10px' }}>{ann.symbol}</span>
              <span>{new Date(ann.date).getFullYear()}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ============================================================================
// SECTION HEADER COMPONENT
// ============================================================================

const SectionHeader = ({ title, subtitle, children }) => (
  <div style={{
    marginBottom: brioStyles.spacingM
  }}>
    <p style={{
      fontSize: brioStyles.fontSizeBase,
      fontWeight: brioStyles.fontWeightLight,
      color: brioColors.grey,
      margin: 0,
      fontStyle: 'italic',
      textAlign: 'left'
    }}>
      {subtitle}
    </p>
    {children && (
      <div style={{ marginTop: brioStyles.spacingS }}>
        {children}
      </div>
    )}
  </div>
);

// ============================================================================
// BRIO FRAME & ANALYTICS SURFACE (reusable layout primitives)
// ============================================================================

const BrioFrame = ({ children, style }) => (
  <div style={{
    padding: brioStyles.spacingL,
    maxWidth: brioStyles.maxWidthContainer,
    ...style
  }}>
    {children}
  </div>
);

const BrioAnalyticsSurface = ({ children, style }) => (
  <div style={{
    background: brioColors.greyLight,
    border: `1px solid ${brioColors.border}`,
    borderRadius: '0',
    padding: brioStyles.spacingL,
    ...style
  }}>
    {children}
  </div>
);

// ============================================================================
// STAGE DATA READINESS BANNER
// ============================================================================
// Shared banner for Stage Intelligence & Capacity Intelligence sections.
// Queries Supabase directly (same pattern as useSalaryLedger) to surface:
//   1. Stage-anchor coverage  (project_stage_entries)
//   2. Ledger window quality  (stage_window_ledger)
//   3. Time-entry recency     (time_entries.date max)
// ============================================================================

const StageDataReadinessBanner = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [metrics, setMetrics] = useState({
    projectsWithStageEntries: 0,
    totalStageEntries: 0,
    countsByType: {},          // { invoice: n, closure: n, actual: n, ... }
    ledgerByBand: {},          // { green: n, amber: n, red: n }
    lastTimeEntryDate: null,
    daysSinceLastEntry: null,
  });

  useEffect(() => {
    let cancelled = false;

    const fetchReadiness = async () => {
      setLoading(true);
      setError(null);

      try {
        // ── 1. Stage anchors coverage ──────────────────────────────────
        // Distinct projects
        const { data: stageRows, error: stageErr } = await supabase
          .from('project_stage_entries')
          .select('project_id, type');

        if (stageErr) throw stageErr;

        const rows = stageRows || [];
        const distinctProjects = new Set(rows.map(r => r.project_id));
        const countsByType = {};
        rows.forEach(r => {
          const t = r.type || 'unknown';
          countsByType[t] = (countsByType[t] || 0) + 1;
        });

        // ── 2. Ledger window quality ───────────────────────────────────
        const { data: ledgerRows, error: ledgerErr } = await supabase
          .from('stage_window_ledger')
          .select('quality_band');

        if (ledgerErr) throw ledgerErr;

        const ledgerByBand = {};
        (ledgerRows || []).forEach(r => {
          const band = (r.quality_band || 'unknown').toLowerCase();
          ledgerByBand[band] = (ledgerByBand[band] || 0) + 1;
        });

        // ── 3. Time-entry recency ──────────────────────────────────────
        const { data: recentEntry, error: recentErr } = await supabase
          .from('time_entries')
          .select('date')
          .order('date', { ascending: false })
          .limit(1)
          .maybeSingle();

        if (recentErr) throw recentErr;

        let lastDate = null;
        let daysSince = null;
        if (recentEntry?.date) {
          lastDate = recentEntry.date;
          const diff = Date.now() - new Date(lastDate).getTime();
          daysSince = Math.floor(diff / (1000 * 60 * 60 * 24));
        }

        if (!cancelled) {
          setMetrics({
            projectsWithStageEntries: distinctProjects.size,
            totalStageEntries: rows.length,
            countsByType,
            ledgerByBand,
            lastTimeEntryDate: lastDate,
            daysSinceLastEntry: daysSince,
          });
        }
      } catch (err) {
        console.error('[StageDataReadinessBanner] fetch error:', err);
        if (!cancelled) setError(err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchReadiness();
    return () => { cancelled = true; };
  }, []);

  // ── Warning logic ──────────────────────────────────────────────────────
  const greenWindows = metrics.ledgerByBand.green || 0;
  const amberWindows = metrics.ledgerByBand.amber || 0;
  const redWindows   = metrics.ledgerByBand.red   || 0;

  // Readiness is driven by benchmark coverage (green windows) and time-entry
  // recency. Manual stage anchors (project_stage_entries) are an OPTIONAL
  // refinement since windows are derived from time entries — their count is
  // shown for context but no longer gates readiness.
  const warnings = [];
  if (!loading && !error) {
    if (greenWindows < 10)
      warnings.push('fewer than 10 high-confidence stage benchmarks');
    if (metrics.daysSinceLastEntry != null && metrics.daysSinceLastEntry > 14)
      warnings.push(`last time entry was ${metrics.daysSinceLastEntry} days ago`);
  }

  const hasWarning = warnings.length > 0;

  // ── Chip helper ────────────────────────────────────────────────────────
  const Chip = ({ label, value, color, title }) => (
    <span title={title} style={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: '6px',
      padding: '4px 12px',
      fontSize: '12px',
      fontWeight: brioStyles.fontWeightRegular,
      fontFamily: brioStyles.fontFamily,
      color: color || brioColors.black,
      background: color
        ? `${color}14`        // 8% opacity of the chip colour
        : brioColors.greyLight,
      border: `1px solid ${color ? `${color}33` : brioColors.border}`,
      borderRadius: '0',
      lineHeight: 1,
      whiteSpace: 'nowrap',
      cursor: title ? 'help' : 'default',
    }}>
      <span style={{ fontWeight: brioStyles.fontWeightMedium }}>{value}</span>
      <span style={{ opacity: 0.7 }}>{label}</span>
    </span>
  );

  // ── Band dot helper ────────────────────────────────────────────────────
  const bandColor = (band) => {
    if (band === 'green') return '#2e7d32';
    if (band === 'amber') return '#e65100';
    if (band === 'red')   return '#c62828';
    return brioColors.grey;
  };

  // ── Render ─────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <BrioAnalyticsSurface style={{ marginBottom: brioStyles.spacingM }}>
        <p style={{
          fontSize: brioStyles.fontSizeBase,
          fontWeight: brioStyles.fontWeightLight,
          color: brioColors.grey,
          margin: 0,
          fontStyle: 'italic',
        }}>
          loading data readiness…
        </p>
      </BrioAnalyticsSurface>
    );
  }

  if (error) {
    return (
      <BrioAnalyticsSurface style={{
        marginBottom: brioStyles.spacingM,
        background: '#fef2f2',
        borderColor: '#fecaca',
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: brioStyles.spacingXS,
        }}>
          <AlertTriangle size={16} style={{ color: '#c62828', flexShrink: 0 }} />
          <p style={{
            fontSize: brioStyles.fontSizeBase,
            fontWeight: brioStyles.fontWeightRegular,
            color: '#c62828',
            margin: 0,
          }}>
            could not load readiness data — {error}
          </p>
        </div>
      </BrioAnalyticsSurface>
    );
  }

  return (
    <div style={{ marginBottom: brioStyles.spacingM }}>
      {/* ── Warning banner ──────────────────────────────────────────── */}
      {hasWarning && (
        <div style={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: brioStyles.spacingXS,
          padding: brioStyles.spacingS,
          marginBottom: brioStyles.spacingS,
          background: 'rgba(230, 81, 0, 0.06)',
          border: '1px solid rgba(230, 81, 0, 0.18)',
          borderRadius: '0',
        }}>
          <AlertTriangle size={16} style={{
            color: '#e65100',
            flexShrink: 0,
            marginTop: '1px',
          }} />
          <div>
            <p style={{
              fontSize: brioStyles.fontSizeBase,
              fontWeight: brioStyles.fontWeightMedium,
              color: '#e65100',
              margin: 0,
              marginBottom: '4px',
            }}>
              data readiness — attention needed
            </p>
            <p style={{
              fontSize: '12px',
              fontWeight: brioStyles.fontWeightLight,
              color: brioColors.grey,
              margin: 0,
              lineHeight: 1.5,
            }}>
              {warnings.join(' · ')}
            </p>
          </div>
        </div>
      )}

      {/* ── Metrics surface ─────────────────────────────────────────── */}
      <BrioAnalyticsSurface>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: brioStyles.spacingXS,
          marginBottom: brioStyles.spacingS,
        }}>
          {!hasWarning && (
            <CheckCircle size={14} style={{ color: '#2e7d32', flexShrink: 0 }} />
          )}
          <p style={{
            fontSize: '12px',
            fontWeight: brioStyles.fontWeightMedium,
            color: brioColors.grey,
            margin: 0,
            textTransform: 'lowercase',
            letterSpacing: '0.3px',
          }}>
            data readiness
          </p>
        </div>

        {/* One-line explanation of what "readiness" means here. */}
        <p style={{
          fontSize: '11px',
          fontWeight: brioStyles.fontWeightLight,
          color: brioColors.grey,
          margin: 0,
          marginBottom: brioStyles.spacingS,
          lineHeight: 1.5,
        }}>
          whether there's enough recent, good-quality time data to trust the stage-timing analytics below.
        </p>

        {/* Stage-timing benchmarks, graded by data confidence (derived from
            time entries). High-confidence count is what gates readiness. */}
        <div style={{ marginBottom: '12px' }}>
          <div style={{
            fontSize: '11px',
            color: brioColors.grey,
            marginBottom: '6px',
          }}>
            stage-timing benchmarks <span style={{ opacity: 0.7 }}>(by data confidence)</span>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
            <Chip label="high confidence" value={greenWindows} color={bandColor('green')}
              title="Stage-duration benchmarks with strong supporting time data. At least 10 of these are needed for reliable stage-timing analytics." />
            <Chip label="medium confidence" value={amberWindows} color={bandColor('amber')}
              title="Benchmarks with partial supporting data — usable but less certain." />
            <Chip label="low confidence" value={redWindows} color={bandColor('red')}
              title="Benchmarks with thin supporting data — treat with caution." />
          </div>
        </div>

        {/* Time-entry recency */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '12px' }}>
          {metrics.daysSinceLastEntry != null ? (
            <Chip
              label="days since last time entry"
              value={metrics.daysSinceLastEntry}
              color={metrics.daysSinceLastEntry > 14 ? '#e65100' : '#2e7d32'}
              title="Stage analytics rely on up-to-date time tracking. A gap over 14 days flags the data as stale."
            />
          ) : (
            <Chip label="time entries" value="none" color="#c62828" />
          )}
          {metrics.lastTimeEntryDate && (
            <Chip label="last entry" value={metrics.lastTimeEntryDate} />
          )}
        </div>

        {/* Optional manual stage markers — context only, do not gate readiness. */}
        {metrics.totalStageEntries > 0 && (
          <p style={{
            fontSize: '11px',
            fontWeight: brioStyles.fontWeightLight,
            color: brioColors.grey,
            margin: 0,
            lineHeight: 1.5,
          }}>
            {metrics.totalStageEntries} manual stage marker{metrics.totalStageEntries === 1 ? '' : 's'} across{' '}
            {metrics.projectsWithStageEntries} project{metrics.projectsWithStageEntries === 1 ? '' : 's'}
            {Object.keys(metrics.countsByType).length > 0 && (
              <> ({Object.entries(metrics.countsByType).map(([t, c]) => `${c} ${t}`).join(', ')})</>
            )}
            {' '}— optional, not required for readiness.
          </p>
        )}
      </BrioAnalyticsSurface>
    </div>
  );
};

// ============================================================================
// TAB NAVIGATION COMPONENT
// ============================================================================

const TabNav = ({ tabs, activeTab, onChange }) => (
  <div style={{ 
    display: 'flex', 
    gap: brioStyles.spacingXS,
    borderBottom: `1px solid ${brioColors.border}`,
    marginBottom: brioStyles.spacingM
  }}>
    {tabs.map(tab => (
      <button
        key={tab.id}
        onClick={() => onChange(tab.id)}
        style={{
          padding: `${brioStyles.spacingXS} ${brioStyles.spacingS}`,
          background: 'transparent',
          border: 'none',
          borderBottom: activeTab === tab.id ? `2px solid ${brioColors.gold}` : '2px solid transparent',
          color: activeTab === tab.id ? brioColors.black : brioColors.grey,
          fontSize: brioStyles.fontSizeBase,
          fontWeight: activeTab === tab.id ? brioStyles.fontWeightMedium : brioStyles.fontWeightRegular,
          cursor: 'pointer',
          textTransform: 'lowercase',
          fontFamily: brioStyles.fontFamily,
          transition: 'all 0.2s'
        }}
      >
        {tab.label}
      </button>
    ))}
  </div>
);

// ============================================================================
// FILTER COMPONENT
// ============================================================================

const _FilterBar = ({ filters, activeFilters, onChange }) => ( // eslint-disable-line no-unused-vars
  <div style={{ 
    display: 'flex', 
    gap: brioStyles.spacingS,
    flexWrap: 'wrap',
    marginBottom: brioStyles.spacingM,
    padding: brioStyles.spacingS,
    background: brioColors.greyLight,
    borderRadius: '0'
  }}>
    {filters.map(filter => (
      <button
        key={filter.id}
        onClick={() => onChange(filter.id)}
        style={{
          padding: `6px ${brioStyles.spacingS}`,
          background: activeFilters.includes(filter.id) ? brioColors.gold : brioColors.white,
          border: `1px solid ${brioColors.border}`,
          color: activeFilters.includes(filter.id) ? brioColors.white : brioColors.black,
          fontSize: brioStyles.fontSizeBase,
          fontWeight: brioStyles.fontWeightRegular,
          cursor: 'pointer',
          textTransform: 'lowercase',
          fontFamily: brioStyles.fontFamily,
          borderRadius: '0',
          transition: 'all 0.2s'
        }}
      >
        {filter.label}
      </button>
    ))}
  </div>
);

// ============================================================================
// MAIN ANALYTICS DASHBOARD COMPONENT
// ============================================================================


export {
  ExportButton, ExportPanel, DateRangeFilter, annotationTypes, AnnotationManager,
  CustomTooltip, fmtPercent, StageHoursChart, ChartExpandWrapper, KPITile,
  SimpleTrendLine, SectionHeader, BrioFrame, BrioAnalyticsSurface,
  StageDataReadinessBanner, TabNav, _fmtCurrFallback,
};
